import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Import the ModelLoadProgress type from MobileQwen3Manager
import { ModelLoadProgress } from "./MobileQwen3Manager";

// Enhanced structure returned from /dhp-status
interface DhpStatus {
  task_id: string | null;
  task_status: string;
  status: string;   // "not_started" | "running" | "completed" | "error"
  progress: number; // 0..100 (phase-based)
  current_processor: string | null; // "gdrive" | "gmail" | "onedrive" | "outlook" | "desktop"
  current_phase: number; // 1-4
  total_processors: number;
  model_loaded: boolean; // ⭐ NEW
  max_user_integrations: number | null; // ⭐ NEW
  error?: string;
}

interface ProgressIndicatorProps {
  theme: string;
  modelProgress: ModelLoadProgress | null;
  modelReady: boolean;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ 
  theme, 
  modelProgress, 
  modelReady 
}) => {
  const [dhpStatus, setDhpStatus] = useState<DhpStatus>({
    task_id: null,
    task_status: "NOT_FOUND",
    status: "not_started", 
    progress: 0,
    current_processor: null,
    current_phase: 0,
    total_processors: 0,
    model_loaded: false, // ⭐ NEW
    max_user_integrations: null, // ⭐ NEW
  });

  // State to track the highest progress seen *during the current run*
  const [runProgress, setRunProgress] = useState(0);

  const [isPolling, setIsPolling] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchDhpStatus() {
      // Don't fetch if already completed
      if (isCompleted) {
        console.log("🛑 POLLING_STOPPED: Process already completed");
        return;
      }

      try {
        const res = await fetch("/dhp-status", { credentials: "include" });
        if (res.ok) {
          const data: DhpStatus = await res.json();
          console.log(`📊 STATUS_UPDATE: ${data.status}, progress: ${data.progress}%, processor: ${data.current_processor}, phase: ${data.current_phase}, model_loaded: ${data.model_loaded}`);
          
          // REVISED FIX: Implement a smarter, conditional progress guard
          if (data.status === "running") {
            // While running, only let the progress increase (the high-water mark)
            setRunProgress(prevRunProgress => Math.max(prevRunProgress, data.progress));
          } else {
            // When not running (completed, error, not_started), trust the backend value directly.
            // This allows the percentage to correctly drop after an integration is removed.
            setRunProgress(data.progress);
          }

          setDhpStatus(data);
          
          // Check if DHP processing is completed
          if (data.status === "completed" || data.progress >= 100) {
            console.log("✅ DHP_COMPLETED: Stopping polling");
            setIsCompleted(true);
            setIsPolling(false);
            
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          } else if (data.status === "error") {
            // ⭐ FIX: Errors are terminal — stop polling immediately
            console.log("❌ DHP_ERROR: Stopping polling (terminal state)");
            setIsPolling(false);
            
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          } else if (data.status === "running") {
            setIsPolling(true);
          } else if (data.status === "not_started" && data.total_processors === 0 && !data.max_user_integrations) {
            // Scenario A: No integrations at all — nothing to wait for, show 0%
            console.log("ℹ️ NO_INTEGRATIONS: No processors configured, stopping polling");
            setIsPolling(false);
            
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          } else {
            // DHP is spinning up or in an intermediate state — keep polling
            console.log(`⚠️ STATUS_OTHER: ${data.status} - continuing to poll`);
            setIsPolling(true);
          }
        } else {
          console.warn("Failed to fetch /dhp-status; HTTP:", res.status);
          if (!isCompleted) {
            console.log("🔄 FETCH_ERROR: Retrying...");
          }
        }
      } catch (err) {
        console.error("Error fetching /dhp-status:", err);
        if (!isCompleted) {
          console.log("🔄 NETWORK_ERROR: Retrying...");
        }
      }
    }

    // Initial fetch
    fetchDhpStatus();

    // Set up polling with proper cleanup
    if (isPolling && !isCompleted) {
      intervalRef.current = setInterval(fetchDhpStatus, 10000);
      console.log("🔄 POLLING_STARTED: Checking every 10 seconds");
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        console.log("🧹 CLEANUP: Interval cleared");
      }
    };
  }, [isPolling, isCompleted]);

  // ⭐ Track whether we've already notified the backend about model loading
  const modelNotifiedRef = useRef(false);

  // ⭐ Notify backend when model finishes loading
  useEffect(() => {
    if (modelReady && !modelNotifiedRef.current) {
      modelNotifiedRef.current = true; // Prevent duplicate calls
      console.log("🔔 MODEL_READY: Sending notification to backend...");
      fetch("/mark-model-loaded", {
        method: "POST",
        credentials: "include"
      })
        .then(res => {
          if (res.ok) {
            console.log("✅ MODEL_LOADED: Backend notified");
          } else {
            console.error(`❌ MODEL_LOADED_FAILED: Backend responded with status ${res.status}`);
            modelNotifiedRef.current = false; // Allow retry on failure
            res.json().then(err => console.error("Error details:", err)).catch(() => {});
          }
        })
        .catch(err => {
          console.error("Error marking model as loaded:", err);
          modelNotifiedRef.current = false; // Allow retry on network error
        });
    }
  }, [modelReady]);

  // ========================================================================
  // COMBINED PROGRESS CALCULATION
  // ========================================================================
  
  // ⭐ SIMPLIFIED: Backend now handles all progress calculation including model loading
  // Use the new `runProgress` state for display
  const displayPct = Math.min(Math.max(runProgress, 0), 100);
  const displayPctRounded = Math.round(displayPct);

  // ========================================================================
  // DYNAMIC STATUS TEXT
  // ========================================================================
  
  let statusText = "Updating Profile!";
  let statusClass = "pulsing";
  
  // Check if everything is completed (both model and DHP)
  const isProcessCompleted = modelReady && (isCompleted || dhpStatus.status === "completed" || dhpStatus.progress >= 100);
  
  if (isProcessCompleted) {
    statusText = "Profile Updated!";
    statusClass = "";
  } else if (!dhpStatus.model_loaded) {
    // ⭐ CHANGED: Check backend model_loaded flag instead of frontend modelReady
    statusText = "AI Assistant Loading...";
    statusClass = "pulsing";
  } else if (dhpStatus.status === "running" && dhpStatus.current_processor) {
    // Model is ready, DHP is running - show which data source is loading
    switch (dhpStatus.current_processor) {
      case "gdrive":
        statusText = "Google Drive Loading...";
        break;
      case "gmail":
        statusText = "Gmail Loading...";
        break;
      case "onedrive":
        statusText = "OneDrive Loading...";
        break;
      case "outlook":
        statusText = "Outlook Loading...";
        break;
      case "desktop":
        statusText = "Desktop Loading...";
        break;
      default:
        statusText = "Processing Files";
    }
    statusClass = "pulsing";
  } else if (dhpStatus.status === "not_started") {
    statusText = "Starting...";
    statusClass = "pulsing";
  } else if (dhpStatus.status === "error") {
    statusText = "Update Error";
    statusClass = "";
  }

  // ========================================================================
  // CIRCULAR PROGRESS BAR RENDERING
  // ========================================================================
  
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (displayPct / 100) * circumference;

  const textColor = theme === "dark" ? "#FFFFFF" : "#333333";
  const statusTextColor = theme === "dark" ? "#FFFFFF" : "#555555";
  const circleStrokeColor = theme === "dark" ? "#555555" : "#EEEEEE";
  
  // Different colors based on completion state
  const progressStrokeColor = isProcessCompleted ? "#28a745" : "green";

  return (
    <div
      style={{ 
        textAlign: "center", 
        cursor: "pointer", 
        marginRight: "8px",  // was 10px
        position: "static",
        display: "inline-block"
      }}
      onClick={() => navigate("/my-profile")}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap"
        rel="stylesheet"
      />

      <div
        className={`status-text ${statusClass}`}
        style={{
          marginBottom: "2px",    // Closer to circle (was 4px)
          fontSize: "9px",        // Smaller text (was 10px) - 8px was small
          fontWeight: 450,        // Lighter weight (was 500) - 400 was light
          color: statusTextColor,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        {statusText}
      </div>

      <div
        className="dhp-widget"
        style={{
          width: "50px",
          height: "50px",
          fontFamily: "'Inter', sans-serif",
          position: "relative",
          display: "inline-block",
        }}
      >
        <svg width={50} height={50} style={{ transform: "rotate(-90deg)" }}>
          <circle
            stroke={circleStrokeColor}
            fill="transparent"
            strokeWidth={4}
            r={radius}
            cx={25}
            cy={25}
          />
          <circle
            stroke={progressStrokeColor}
            fill="transparent"
            strokeWidth={4}
            r={radius}
            cx={25}
            cy={25}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{
              transition: "stroke-dashoffset 0.35s",
              transform: "rotate(0deg)",
              transformOrigin: "50% 50%",
            }}
          />
        </svg>

        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "14px",
            fontWeight: 600,
            color: textColor,
          }}
        >
          {displayPctRounded}%
        </div>
      </div>

      <style>{`
        @keyframes pulsingText {
          0%   { opacity: 1;   }
          50%  { opacity: 0.5; }
          100% { opacity: 1;   }
        }
        .pulsing {
          animation: pulsingText 1.2s infinite;
        }
      `}</style>
    </div>
  );
};